import {
  Container,
  Stack,
  Box,
  Text,
  SimpleGrid,
  Tag,
  Wrap,
  WrapItem,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Tilt from "react-parallax-tilt";
import SectionHeading from "./SectionHeading";
import t from "../translations";

const MotionBox = motion(Box);

function SkillCategory({ category, index, color }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const cardBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const glowColor = useColorModeValue(
    `var(--chakra-colors-${color}-200)`,
    `var(--chakra-colors-${color}-400)`
  );

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay: index * 0.1, ease: "easeOut" }}
    >
      <Tilt
        tiltMaxAngleX={8}
        tiltMaxAngleY={8}
        glareEnable
        glareMaxOpacity={0.12}
        glarePosition="all"
        glareBorderRadius="12px"
        scale={1.02}
      >
        <Box
          bg={cardBg}
          p={{ base: 4, md: 6 }}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          h="100%"
          transition="all 0.3s ease"
          _hover={{
            boxShadow: `0 0 20px 4px ${glowColor}`,
            borderColor: `${color}.400`,
          }}
        >
          <Text
            fontWeight={600}
            fontSize="sm"
            color={`${color}.400`}
            mb={4}
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {category.title}
          </Text>
          <Wrap spacing={2}>
            {category.items.map((skill) => (
              <WrapItem key={skill}>
                <Tag
                  size="md"
                  variant="subtle"
                  colorScheme={color}
                  borderRadius="full"
                  transition="all 0.2s"
                  _hover={{
                    transform: "scale(1.08)",
                    boxShadow: `0 0 8px 2px ${glowColor}`,
                  }}
                >
                  {skill}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      </Tilt>
    </MotionBox>
  );
}

export default function Skills({ color }) {
  return (
    <Container maxW="4xl" id="skills" py={{ base: 16, md: 24 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <SectionHeading>{t.skills.heading}</SectionHeading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} px={4}>
          {t.skills.categories.map((cat, i) => (
            <SkillCategory
              key={cat.title}
              category={cat}
              index={i}
              color={color}
            />
          ))}
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
